<template>
  <div class="ui-date-picker-panel w-64 select-none">
    <div class="mb-2 flex items-center justify-between px-1">
      <UiButton variant="text" size="small" @click.stop="prevMonth">
        <UiIcon name="chevron-left" />
      </UiButton>
      <div class="text-sm font-medium capitalize">
        {{ currentMonthName }} {{ currentYear }}
      </div>
      <UiButton variant="text" size="small" @click.stop="nextMonth">
        <UiIcon name="chevron-right" />
      </UiButton>
    </div>

    <div class="mb-2 grid grid-cols-7 gap-1">
      <div
        v-for="day in weekDays"
        :key="day"
        class="flex h-8 w-8 items-center justify-center text-center text-xs font-medium text-gray-500 capitalize"
      >
        {{ day }}
      </div>
    </div>

    <div class="grid grid-cols-7 gap-1">
      <button
        v-for="(date, i) in days"
        :key="i"
        type="button"
        :disabled="date.isDisabled"
        class="flex h-8 w-8 items-center justify-center rounded-full text-sm transition-all duration-200"
        :class="{
          'text-gray-300': !date.isCurrentMonth,
          'cursor-not-allowed text-gray-200': date.isDisabled,
          'hover:bg-gray-100':
            !date.isSelected && date.isCurrentMonth && !date.isDisabled,
          'bg-primary ring-primary z-10 scale-110 transform font-bold text-white ring-2 ring-offset-1':
            date.isSelected,
          'border-primary text-primary bg-primary/20 border-2 font-extrabold shadow-sm':
            date.isToday && !date.isSelected,
        }"
        @click.stop="!date.isDisabled && selectDate(date.dateString)"
      >
        {{ date.day }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'

import UiButton from '@/shared/components/ui/UiButton.vue'
import UiIcon from '@/shared/components/ui/UiIcon.vue'

const props = defineProps<{
  value?: string | null
  modelValue?: string | null
  pickerDate?: string | null
  /** Inclusive lower bound ('YYYY-MM-DD'); earlier days render disabled. */
  min?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void
  (e: 'validate'): void
}>()

const currentValue = computed(() => {
  const v = props.modelValue ?? props.value
  return v != null && v !== '' ? v : null
})

const currentCursor = ref(
  currentValue.value != null
    ? dayjs(currentValue.value)
    : props.pickerDate != null
      ? dayjs(props.pickerDate)
      : dayjs(),
)

watch(
  () => currentValue.value,
  (newVal) => {
    if (newVal != null) {
      currentCursor.value = dayjs(newVal)
    }
  },
)

const currentMonthName = computed(() => currentCursor.value.format('MMMM'))
const currentYear = computed(() => currentCursor.value.format('YYYY'))

const weekDays = computed(() => {
  const names = []
  for (let i = 1; i <= 7; i++) {
    // day(1) is Monday, day(0) is Sunday
    names.push(
      dayjs()
        .day(i === 7 ? 0 : i)
        .format('dd'),
    )
  }
  return names
})

const days = computed(() => {
  const startOfMonth = currentCursor.value.startOf('month')

  const startDay = startOfMonth.day()
  const paddingBefore = startDay === 0 ? 6 : startDay - 1

  const calendarStart = startOfMonth.subtract(paddingBefore, 'day')

  const calendarDays = []
  const todayStr = dayjs().format('YYYY-MM-DD')
  const selectedStr =
    currentValue.value != null
      ? dayjs(currentValue.value).format('YYYY-MM-DD')
      : null
  const minStr =
    props.min != null && props.min !== ''
      ? dayjs(props.min).format('YYYY-MM-DD')
      : null

  for (let i = 0; i < 42; i++) {
    const current = calendarStart.add(i, 'day')
    const dateStr = current.format('YYYY-MM-DD')
    calendarDays.push({
      day: current.date(),
      dateString: dateStr,
      isCurrentMonth: current.month() === currentCursor.value.month(),
      isToday: dateStr === todayStr,
      isSelected: dateStr === selectedStr,
      isDisabled: minStr != null && dateStr < minStr,
    })
  }

  return calendarDays
})

function prevMonth() {
  currentCursor.value = currentCursor.value.subtract(1, 'month')
}

function nextMonth() {
  currentCursor.value = currentCursor.value.add(1, 'month')
}

function selectDate(dateString: string) {
  emit('update:modelValue', dateString)
  emit('validate')
}
</script>
